import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade4EngineeringLesson.css";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

/** Helper to shuffle arrays for quizzes */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Engineering4DDemo:
 * Cycles among three 3D models:
 *   - Crane
 *   - Robot Arm
 *   - Solar Panel
 */
function Engineering4DDemo() {
  const items = [
    {
      name: "Crane",
      description: "Cranes help lift heavy materials in construction.",
      example: "Engineers design stable towers with counterweights!",
    },
    {
      name: "Robot Arm",
      description: "Robotic arms automate tasks like assembly or painting!",
      example: "Engineers program precise movements with sensors and motors.",
    },
    {
      name: "Solar Panel",
      description: "Solar panels convert sunlight into electricity!",
      example: "Engineers optimize the panel angle to capture maximum sun.",
    },
  ];

  const [index, setIndex] = useState(0);

  const nextItem = () => {
    setIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className="g4eng-3d-demo-container">
      <h4>3D Engineering Demo: {items[index].name}</h4>
      <p className="g4eng-demo-description">{items[index].description}</p>
      <p className="g4eng-demo-example">
        <strong>Fun Fact:</strong> {items[index].example}
      </p>
      <button className="g4eng-3d-next-btn" onClick={nextItem}>
        See Another Example
      </button>

      <div style={{ width: "300px", height: "200px", margin: "10px auto", background: "#f0f0f0" }}>
        <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />

          {/* Simple ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#ddd" />
          </mesh>

          {/* Model by name */}
          <EngineeringShape name={items[index].name} />
        </Canvas>
      </div>
    </div>
  );
}

/**
 * EngineeringShape:
 *  - "Crane": a vertical tower + horizontal jib + small hook
 *  - "Robot Arm": stacked boxes/cylinders
 *  - "Solar Panel": stand + rectangular panel
 */
function EngineeringShape({ name }) {
  if (name === "Crane") {
    return (
      <group>
        {/* Tower as a tall box */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[0.2, 2, 0.2]} />
          <meshStandardMaterial color="#ffb74d" />
        </mesh>
        {/* Jib: horizontal arm on top */}
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[1.2, 0.1, 0.1]} />
          <meshStandardMaterial color="#f57c00" />
        </mesh>
        {/* Hook: small box hanging from arm's end */}
        <mesh position={[0.5, 0.6, 0]}>
          <boxGeometry args={[0.05, 0.4, 0.05]} />
          <meshStandardMaterial color="#6d4c41" />
        </mesh>
      </group>
    );
  } 
  else if (name === "Robot Arm") {
    return (
      <group>
        {/* Base */}
        <mesh position={[0, -0.5, 0]}>
          <cylinderGeometry args={[0.4, 0.4, 0.2, 16]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        {/* Arm segment 1 */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[0.2, 1, 0.2]} />
          <meshStandardMaterial color="#607d8b" />
        </mesh>
        {/* Arm joint => pivot on top of segment1 */}
        <mesh position={[0, 0.5, 0]}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshStandardMaterial color="#546e7a" />
        </mesh>
        {/* Arm segment 2 => pivot from the joint */}
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[0.2, 1, 0.2]} />
          <meshStandardMaterial color="#455a64" />
        </mesh>
        {/* "Gripper" as a small box on top */}
        <mesh position={[0, 1.5, 0]}>
          <boxGeometry args={[0.15, 0.15, 0.15]} />
          <meshStandardMaterial color="#cfd8dc" />
        </mesh>
      </group>
    );
  } 
  else if (name === "Solar Panel") {
    return (
      <group>
        {/* Pole */}
        <mesh>
          <cylinderGeometry args={[0.05, 0.05, 1.5, 16]} />
          <meshStandardMaterial color="#757575" />
        </mesh>
        {/* Panel: box or plane at top, angled */}
        <mesh rotation={[-Math.PI / 6, 0, 0]} position={[0, 0.75, 0]}>
          <boxGeometry args={[1.2, 0.02, 0.8]} />
          <meshStandardMaterial color="#37474f" />
        </mesh>
      </group>
    );
  }

  // no shape
  return null;
}

/** 
 * Additional Challenge area, e.g. "Sustainability Challenge"
 * or "Architecture Challenge" 
 */
function EngineeringChallenge() {
  const challenges = [
    {
      name: "Construct a Robot Helper",
      description: "What tasks can a home robot assist with? How to design it safely?",
      example: "Focus on stability, sensors, and friendly user interface!",
    },
    {
      name: "Eco-friendly House",
      description: "How do we reduce energy usage and carbon footprint?",
      example: "Use solar panels, insulation, and energy-efficient designs!",
    },
    {
      name: "Efficient Transport",
      description: "How to make vehicles consume less fuel or go electric?",
      example: "Lightweight materials and aerodynamic shapes help a lot!",
    },
  ];
  const [index, setIndex] = useState(0);

  const nextChallenge = () => {
    setIndex((prev) => (prev + 1) % challenges.length);
  };

  return (
    <div className="g4eng-challenge-container">
      <h4>Engineering Challenge: {challenges[index].name}</h4>
      <p className="g4eng-challenge-description">{challenges[index].description}</p>
      <p className="g4eng-challenge-example">
        <strong>Tip:</strong> {challenges[index].example}
      </p>
      <button className="g4eng-next-challenge-btn" onClick={nextChallenge}>
        See Another Challenge
      </button>
    </div>
  );
}

/** Main Grade4EngineeringLesson: two topics, each with 25 Q => total 50 in final quiz. */
export default function Grade4EngineeringLesson() {
  const [selectedTopic, setSelectedTopic] = useState(null);

  /** Lesson Content */
  const lessonContent = {
    advancedProblemSolving: {
      title: "Advanced Problem Solving",
      description: "Learn how engineers solve complex, real-world problems!",
      example: "Example: Designing faster trains that use less energy!",
      steps: [
        "Engineers break big problems into smaller tasks.",
        "They research existing solutions and gather requirements.",
        "They run simulations or advanced calculations to test ideas.",
        "They incorporate safety, cost, and environmental considerations.",
        <Engineering4DDemo key="demo-aps" />,
      ],
    },
    roboticsAutomation: {
      title: "Robotics & Automation",
      description: "Explore how robots automate tasks to help people!",
      example: "Example: Robotic arms assemble cars in factories!",
      steps: [
        "Robots have sensors, actuators (motors), and a control system (computer).",
        "Automation can speed up production and reduce repetitive human tasks.",
        "Engineers program robots for precise movements and safety features.",
        "Machine learning can help robots adapt or learn new tasks!",
        <EngineeringChallenge key="challenge-ra" />,
      ],
    },
  };

  /** Quiz Pools => 25 each */
  // 1) advancedProblemSolvingPool
  const advancedProblemSolvingPool = [
    {
      question: "What is a key step in advanced problem solving?",
      correctAnswer: "Breaking the problem into smaller parts",
      options: [
        "Guessing until something works",
        "Ignoring all data",
        "Breaking the problem into smaller parts",
      ],
    },
    {
      question: "Engineers often run simulations to?",
      correctAnswer: "Test ideas virtually before building",
      options: [
        "Waste time with fancy computers",
        "Ignore real test results",
        "Test ideas virtually before building",
      ],
    },
    // Add more to reach 25...
    {
      question: "When considering cost and safety, engineers should?",
      correctAnswer: "Balance budget with safety & reliability",
      options: [
        "Always pick cheapest design only",
        "Ignore safety if time is short",
        "Balance budget with safety & reliability",
      ],
    },
  ];

  // 2) roboticsAutomationPool
  const roboticsAutomationPool = [
    {
      question: "What do sensors do for a robot?",
      correctAnswer: "They let the robot detect things like distance or touch",
      options: [
        "They remove all tasks from humans",
        "They let the robot detect things like distance or touch",
        "They store the robot’s energy",
      ],
    },
    {
      question: "Why use robots in factories?",
      correctAnswer: "They can handle repetitive tasks quickly and accurately",
      options: [
        "They only slow production",
        "They are purely decorative",
        "They can handle repetitive tasks quickly and accurately",
      ],
    },
    // Add more to reach 25...
    {
      question: "Machine learning in robots can?",
      correctAnswer: "Help robots adapt or learn from new data",
      options: [
        "Remove the need for all sensors",
        "Help robots adapt or learn from new data",
        "Make them forget everything",
      ],
    },
  ];

  // Quick Quiz & Final Quiz states
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  /** Timer for final quiz */
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  /** Generate Quick Quiz */
  const generateQuiz = (topic) => {
    let questionPool;
    if (topic === "advancedProblemSolving") {
      questionPool = advancedProblemSolvingPool;
    } else {
      questionPool = roboticsAutomationPool;
    }
    const item = questionPool[Math.floor(Math.random() * questionPool.length)];
    setQuiz({
      question: item.question,
      correctAnswer: item.correctAnswer,
      options: shuffleArray([...item.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  /** Quick quiz answer */
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // optionally fetch new question after a short delay
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  /** Start Final Quiz => all 50 Qs */
  const startFinalQuiz = () => {
    const combined = [...advancedProblemSolvingPool, ...roboticsAutomationPool];
    const finalSet = shuffleArray(combined);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  /** Final quiz answer */
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex]?.correctAnswer === choice) {
      setFinalQuizScore((prev) => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex((prev) => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g4eng-page">
      <h1 className="g4eng-title">Grade 4 Engineering Lessons</h1>
      <p className="g4eng-description">Select a topic to explore advanced engineering concepts:</p>

      {/* Topic Buttons */}
      <div className="g4eng-topics">
        <button
          className="g4eng-topic-card"
          onClick={() => {
            setSelectedTopic("advancedProblemSolving");
            generateQuiz("advancedProblemSolving");
          }}
        >
          Advanced Problem Solving
        </button>
        <button
          className="g4eng-topic-card"
          onClick={() => {
            setSelectedTopic("roboticsAutomation");
            generateQuiz("roboticsAutomation");
          }}
        >
          Robotics &amp; Automation
        </button>
      </div>

      {selectedTopic && (
        <div className="g4eng-lesson-quiz-container">
          {/* Lesson Content (left) */}
          <div className="g4eng-lesson-content">
            <h2 className="g4eng-lesson-title">{lessonContent[selectedTopic].title}</h2>
            <p className="g4eng-lesson-description">
              {lessonContent[selectedTopic].description}
            </p>
            <p className="g4eng-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g4eng-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Right: Quick Quiz + Final Quiz */}
          <div className="g4eng-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g4eng-quick-quiz-container">
              <h2 className="g4eng-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g4eng-quiz-question">{quiz.question}</p>
                  <div className="g4eng-quiz-options">
                    {quiz.options.map((opt, idx) => (
                      <button
                        key={idx}
                        className={`g4eng-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g4eng-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g4eng-final-quiz-container">
              <h2 className="g4eng-quiz-title">Final Quiz</h2>
              {!finalQuizActive && (
                <button className="g4eng-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {finalQuizActive && (
                <div className="g4eng-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>
                    Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
                  </p>
                  <p className="g4eng-quiz-question">
                    {finalQuizQuestions[finalQuizIndex].question}
                  </p>
                  <div className="g4eng-quiz-options">
                    {finalQuizQuestions[finalQuizIndex].options.map((choice, i) => (
                      <button key={i} onClick={() => handleFinalQuizAnswer(choice)}>
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {finalQuizScore}</p>
                  {/* Show final score once done */}
                  {finalQuizIndex === finalQuizQuestions.length - 1 && (
                    <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Link to="/engineering" className="g4eng-back-button">
        &larr; Back to Engineering Page
      </Link>
    </div>
  );
}
